import React, { useState, useCallback } from 'react';
import Layout from './components/Layout';
import StoryInputForm from './components/StoryInputForm';
import ScriptDisplay from './components/ScriptDisplay';
import LoadingSpinner from './components/LoadingSpinner';
import VideoDisplay from './components/VideoDisplay';
import SeamlessVideoDisplay from './components/SeamlessVideoDisplay';
import SaveOptions from './components/SaveOptions';
import { generateStoryScript, generateVideoFromScript } from './services/geminiService';
import { VideoLength, StoryStructure } from './types';

const App: React.FC = () => {
  const [script, setScript] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [currentVideoLength, setCurrentVideoLength] = useState<VideoLength>(VideoLength.FULL_16S);
  const [currentTopic, setCurrentTopic] = useState<string>('');
  const [currentStructure, setCurrentStructure] = useState<StoryStructure>(StoryStructure.HOOK_TO_END);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState<boolean>(false);
  const [videoError, setVideoError] = useState<string | null>(null);


  const handleGenerateScript = useCallback(async (topic: string, length: VideoLength, structure: StoryStructure) => {
    setIsLoading(true);
    setScript('');
    setError('');
    setVideoUrl(null);
    setVideoError(null);
    setCurrentVideoLength(length);
    setCurrentTopic(topic);
    setCurrentStructure(structure);
    try {
      const generatedScript = await generateStoryScript(topic, length, structure);
      if(generatedScript.startsWith('Error:')) {
        setError(generatedScript);
        setScript('');
      } else {
        setScript(generatedScript);
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
      setError(`Failed to generate script: ${errorMessage}`);
      setScript('');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleGenerateVideo = useCallback(async () => {
    if (!script) {
        setVideoError("Cannot generate a video without a script.");
        return;
    }
    
    setIsGeneratingVideo(true);
    setVideoUrl(null);
    setVideoError(null);

    try {
        const url = await generateVideoFromScript(script);
        setVideoUrl(url);
    } catch(e) {
        const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
        setVideoError(errorMessage);
    } finally {
        setIsGeneratingVideo(false);
    }
  }, [script]);

  const handleGenerateSegment = useCallback(async (segmentNumber: number, scriptContent: string): Promise<string> => {
    try {
      const url = await generateVideoFromScript(scriptContent);
      return url;
    } catch(e) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
      throw new Error(`Failed to generate segment ${segmentNumber}: ${errorMessage}`);
    }
  }, []);

  const WelcomeMessage = () => (
    <div className="mt-8 text-center bg-slate-800/40 p-8 rounded-2xl border border-slate-700">
        <h2 className="text-2xl font-bold text-slate-100 mb-2">Welcome to StoryShot AI</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">
            Simply enter a topic for your video, choose a desired length, and let our AI craft a compelling YouTube Shorts script for you in seconds.
        </p>
    </div>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <StoryInputForm onGenerate={handleGenerateScript} isLoading={isLoading} />
        
        {error && (
            <div className="mt-8 bg-red-900/50 border border-red-700 text-red-200 p-4 rounded-lg">
                <p className="font-bold">An error occurred:</p>
                <p>{error}</p>
            </div>
        )}

        {isLoading && <LoadingSpinner />}

        {!isLoading && !error && script && (
            <>
                <SeamlessVideoDisplay 
                    script={script}
                    videoLength={currentVideoLength}
                    onGenerateSegment={handleGenerateSegment}
                />
                <SaveOptions
                    topic={currentTopic}
                    script={script}
                    videoLength={currentVideoLength}
                    structure={currentStructure}
                />
            </>
        )}

        {!isLoading && !error && !script && <WelcomeMessage />}
      </div>
    </Layout>
  );
};

export default App;
